import { observer } from "mobx-react-lite";
import { Plus, Maximize2, Minimize2, ChevronDown, RotateCcw } from "lucide-react";
import { CardStackIcon } from "@radix-ui/react-icons";

import { VibeChatStore } from "../stores";

import { ChatSessionDropdown } from "./ChatSessionDropdown";

import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import type { ChatSession as APIChatSession } from "@/queries/vibe/useRecentVibeChatSessions";
import { ChatSessionFollowModeButton } from "@/components/VibeChat/Header/ChatSessionFollowModeButton";

interface HeaderProps {
  chatStore: VibeChatStore;
  onClose: () => void;
  onNewChat: () => void;
  onChatSelect: (chat: APIChatSession) => void;
  onDeleteChat?: (chatId: string) => void;
  onDeleteAllChats?: () => void;
  onToggleSize?: () => void;
  onResetPanelLayout?: () => void;
  isMaximized?: boolean;
  onToggleSecondarySidebar?: () => void;
  isSecondarySidebarOpen?: boolean;
}

export const Header = observer(
  ({
    chatStore,
    onClose,
    onNewChat,
    onChatSelect,
    onDeleteChat,
    onDeleteAllChats,
    onToggleSize,
    onResetPanelLayout,
    isMaximized,
    onToggleSecondarySidebar,
    isSecondarySidebarOpen,
  }: HeaderProps) => {
    const currentChat = chatStore.chatSessions.find(c => c.id === chatStore.currentChatId);

    const handleToggleFollowMode = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      chatStore.toggleFollowMode();
    };

    return (
      <div
        data-panel-drag-handle={isMaximized ? undefined : true}
        className={cn(
          "flex touch-none select-none items-center gap-1 border-b border-gray-200 bg-white",
          isMaximized ? "h-16 px-3" : "cursor-grab px-2 py-1.5 active:cursor-grabbing",
        )}
      >
        {!isMaximized && (
          <>
            <BasicTooltip content="Close">
              <Button
                variant="ghost"
                size="icon"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                className="h-7 w-7 shrink-0 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                type="button"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </BasicTooltip>

            <div className="mx-1 h-4 w-px bg-gray-200" />
          </>
        )}

        <ChatSessionDropdown
          chatStore={chatStore}
          currentChat={currentChat}
          onNewChat={onNewChat}
          onChatSelect={onChatSelect}
          onDeleteChat={onDeleteChat}
          onDeleteAllChats={onDeleteAllChats}
          isMaximized={isMaximized}
        />

        <div className="ml-auto flex items-center gap-0.5">
          <BasicTooltip content="New chat">
            <Button
              variant="ghost"
              size="icon"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onNewChat();
              }}
              className="h-7 w-7 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
              type="button"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </BasicTooltip>

          {!isMaximized && onResetPanelLayout && (
            <>
              <BasicTooltip content="Reset panel size and position">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onResetPanelLayout();
                  }}
                  className="h-7 w-7 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                  type="button"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </BasicTooltip>

              <ChatSessionFollowModeButton
                enabled={chatStore.isFollowMode}
                onClick={handleToggleFollowMode}
              />
            </>
          )}

          {onToggleSize && (
            <BasicTooltip content={isMaximized ? "Restore floating chat" : "Open fullscreen chat"}>
              <Button
                variant="ghost"
                size="icon"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleSize();
                }}
                className="h-7 w-7 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                type="button"
              >
                {isMaximized ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
            </BasicTooltip>
          )}
          {onToggleSecondarySidebar && (
            <BasicTooltip
              content={isSecondarySidebarOpen ? "Hide Request Logs" : "Show Request Logs"}
            >
              <Button
                variant={isSecondarySidebarOpen ? "blue" : "ghost"}
                size="icon"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleSecondarySidebar();
                }}
                className="h-7 w-7"
                type="button"
              >
                <CardStackIcon className="h-4 w-4" />
              </Button>
            </BasicTooltip>
          )}
        </div>
      </div>
    );
  },
);

// Export other components for convenience
export { ChatSessionDropdown } from "./ChatSessionDropdown";
export { ChatSessionTrigger } from "./ChatSessionTrigger";
export { ChatSessionItem } from "./ChatSessionItem";
export { ChatSessionList } from "./ChatSessionList";
export { ChatSessionPagination } from "./ChatSessionPagination";
